'use client';

import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { FiClock } from 'react-icons/fi';
import { BiCode, BiRocket, BiTime, BiTrendingUp } from 'react-icons/bi';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineCog, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { RiFlowChart } from 'react-icons/ri';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import ChatbotsNewFeatures from '@/components/blocks/ChatbotsNewFeatures';
import HumanCloneUsage from '@/components/blocks/HumanCloneUsage';
import CTABanner from '@/components/blocks/CTABanner';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import React from 'react';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import { TbTargetArrow } from 'react-icons/tb';
import { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const hero = {
    content: {
      badge: t('features/selfClone/key1'),
      title: t('features/selfClone/key2'),
      description: t('features/selfClone/key3'),
      cta: t('features/selfClone/key4'),
      trustedBy: t('features/selfClone/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071517141879_clone_link_hero.png',
      icon: TbTargetArrow,
      imgOrientation: 'landscape' as IImageOrientation,
      ctaTarget: 'register' as CtaTargetType,
    },
    benefits: [
      {
        icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />,
        text: t('features/humanCloneScenario/hero-benefit1'),
      },
      { icon: <FiClock className="tw-w-5 tw-h-5" />, text: t('features/humanCloneScenario/hero-benefit2') },
    ],
    stats: [
      { value: '2M+', label: t('features/humanCloneScenario/focus1') },
      { value: '76%', label: t('features/humanCloneScenario/focus2') },
      { value: '4-25h', label: t('features/humanCloneScenario/focus3') },
    ],
  };

  const sections = t('features/humanCloneScenario/WhyChooseHumanClone/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = [
    {
      iconicText: t(sections[0].improvement),
      title: t(sections[0].title),
      description: t(sections[0].description),
      icon: <BiTrendingUp className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
      metricLabel: t(sections[0].metricLabel),
      button: t(sections[0].button),
      metric: t(sections[0].metric),
      colorFrom: 'pink' as ColorDefaultChoice,
      colorTo: 'purple' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[1].improvement),
      title: t(sections[1].title),
      description: t(sections[1].description),
      icon: <BiTime className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
      metricLabel: t(sections[1].metricLabel),
      button: t(sections[1].button),
      metric: t(sections[1].metric),
      colorFrom: 'blue' as ColorDefaultChoice,
      colorTo: 'cyan' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[2].improvement),
      title: t(sections[2].title),
      description: t(sections[2].description),
      icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
      metricLabel: t(sections[2].metricLabel),
      button: t(sections[2].button),
      metric: t(sections[2].metric),
      colorFrom: 'green' as ColorDefaultChoice,
      colorTo: 'emerald' as ColorDefaultChoice,
    },
  ];

  const footerSectionBlock = t('features/humanCloneScenario/HumanCloneLinkFeatures/footerSectionBlock', {
    returnObjects: true,
  }) as Array<any>;
  const newFeatures = {
    section: {
      badge: t('features/humanCloneScenario/HumanCloneLinkFeatures/badge'),
      title: t('features/humanCloneScenario/HumanCloneLinkFeatures/title'),
      description: t('features/humanCloneScenario/HumanCloneLinkFeatures/description'),
      subtitle: t('features/humanCloneScenario/HumanCloneLinkFeatures/subtitle'),
      badgeCatchEye: {
        title: t('features/humanCloneScenario/HumanCloneLinkFeatures/badgeCatchEye/title'),
        description: t('features/humanCloneScenario/HumanCloneLinkFeatures/badgeCatchEye/description'),
      },
      footerSectionBlock: footerSectionBlock,
    },
    features: [
      {
        title: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature1/title'),
        description: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature1/description'),
        subtitle: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature1/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071609504963_you_and_clone.png',
        icon: <RiFlowChart className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature2/title'),
        description: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature2/description'),
        subtitle: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature2/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071519422626_form_and_ai.png',
        icon: <HiOutlineCog className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature3/title'),
        description: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature3/description'),
        subtitle: t('features/humanCloneScenario/HumanCloneLinkFeatures/features/feature3/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071519422787_video_and_ai.png',
        icon: <BiCode className="tw-w-6 tw-h-6 tw-text-white" />,
      },
    ],
  };

  return (
    <div className="human-clone-page">
      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={t('features/humanCloneScenario/WhyChooseHumanClone/title')}
            subtitle={t('features/humanCloneScenario/WhyChooseHumanClone/subtitle')}
            description={t('features/humanCloneScenario/WhyChooseHumanClone/description')}
            sections={whyChooseThisBot}
          />
          <ChatbotsNewFeatures
            features={newFeatures.features}
            subtitle={newFeatures.section.subtitle}
            description={newFeatures.section.description}
            title={newFeatures.section.title}
            badge={newFeatures.section.badge}
            badgeCatchEye={newFeatures.section.badgeCatchEye}
            footerSectionBlock={newFeatures.section.footerSectionBlock}
            locale={locale as SupportedLanguages}
          />
        </section>

        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <HumanCloneUsage locale={locale} />
        </section>

        <CTABanner
          title={t('Home/ReadyToGetStarted/cta-title')}
          description={t('Home/ReadyToGetStarted/cta-description')}
          cta={t('Home/ReadyToGetStarted/start-trial-aria')}
          ctaTarget="register"
          locale={locale}
        />

        <SiteFrequentlyAskedQuestionsComponent locale={locale} session="HumanCloneFAQs" />
      </AnimatedGradientWrapper>
    </div>
  );
}
